/**
 * Billing Service — directApiClient mutations (LF-3214)
 */

'use client';

import { ActionResult } from '@/lib/types/actions';
import {
  BillingInfo,
  BillingDetails,
  UpdateBillingDetailsRequest,
  PricingResponseDto,
  ActivationSummaryResponseDto,
  StartTrialRequest,
  StartTrialResponse,
  InvoiceHistoryResponse,
  InvoiceDetail,
  PaymentMethodsResponse,
  SetupIntentResponse
} from './types';
import { directApiClient } from '@/lib/api/direct-client';

export async function getBillingInfoAction(): Promise<
  ActionResult<BillingInfo>
> {
  try {
    const data = await directApiClient.get<BillingInfo>('/account/billing');
    return { success: true, data };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'Failed to fetch billing information'
    };
  }
}

export async function startTrialAction(
  request: StartTrialRequest
): Promise<ActionResult<StartTrialResponse>> {
  try {
    if (!request.successUrl || !request.cancelUrl) {
      return {
        success: false,
        error: 'Success and cancel URLs are required'
      };
    }

    const data = await directApiClient.post<StartTrialResponse>(
      '/account/billing/start-trial',
      request
    );
    return { success: true, data };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to start trial'
    };
  }
}

export async function updateBillingDetailsAction(
  request: UpdateBillingDetailsRequest
): Promise<ActionResult<BillingDetails>> {
  try {
    const data = await directApiClient.patch<BillingDetails>(
      '/account/billing/details',
      request
    );
    return { success: true, data };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'Failed to update billing details'
    };
  }
}

export async function getPricingAction(): Promise<
  ActionResult<PricingResponseDto>
> {
  try {
    const data = await directApiClient.get<PricingResponseDto>(
      '/account/billing/pricing'
    );
    return { success: true, data };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to fetch pricing'
    };
  }
}

export async function getActivationSummaryAction(): Promise<
  ActionResult<ActivationSummaryResponseDto>
> {
  try {
    const data = await directApiClient.get<ActivationSummaryResponseDto>(
      '/account/billing/activation-summary'
    );
    return { success: true, data };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'Failed to fetch activation summary'
    };
  }
}

export async function getInvoiceHistoryAction(): Promise<
  ActionResult<InvoiceHistoryResponse>
> {
  try {
    const data = await directApiClient.get<InvoiceHistoryResponse>(
      '/account/billing/invoices'
    );
    return { success: true, data };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'Failed to fetch invoice history'
    };
  }
}

export async function getInvoiceDetailAction(
  invoiceId: number
): Promise<ActionResult<InvoiceDetail>> {
  try {
    const data = await directApiClient.get<InvoiceDetail>(
      `/account/billing/invoices/${invoiceId}`
    );
    return { success: true, data };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'Failed to fetch invoice detail'
    };
  }
}

/**
 * List the firm's saved cards (GET /account/billing/payment-methods, LF-4605).
 */
export async function getPaymentMethodsAction(): Promise<
  ActionResult<PaymentMethodsResponse>
> {
  try {
    const data = await directApiClient.get<PaymentMethodsResponse>(
      '/account/billing/payment-methods'
    );
    return { success: true, data };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'Failed to load payment methods'
    };
  }
}

/**
 * Mint a SetupIntent (POST /account/billing/setup-intent, LF-4605). The
 * returned client secret is handed to the Payment Element for `confirmSetup`.
 */
export async function createSetupIntentAction(): Promise<
  ActionResult<SetupIntentResponse>
> {
  try {
    const data = await directApiClient.post<SetupIntentResponse>(
      '/account/billing/setup-intent'
    );
    return { success: true, data };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error ? error.message : 'Failed to start card setup'
    };
  }
}

/**
 * Set a saved card as the invoice default (POST …/payment-methods/:id/default).
 */
export async function setDefaultPaymentMethodAction(
  paymentMethodId: string
): Promise<ActionResult<void>> {
  try {
    await directApiClient.post<void>(
      `/account/billing/payment-methods/${paymentMethodId}/default`
    );
    return { success: true, data: undefined };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'Failed to set the default card'
    };
  }
}

/**
 * Remove a saved card (DELETE …/payment-methods/:id). The BE blocks removing
 * the last card on an active/trialling firm — its 400 message surfaces here.
 */
export async function removePaymentMethodAction(
  paymentMethodId: string
): Promise<ActionResult<void>> {
  try {
    await directApiClient.delete<void>(
      `/account/billing/payment-methods/${paymentMethodId}`
    );
    return { success: true, data: undefined };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error ? error.message : 'Failed to remove the card'
    };
  }
}
